import * as Sentry from '@sentry/browser';
import get from 'lodash/get.js';
import uuid from 'uuid/v4.js';

import moment from '@/plugins/vue-moment.js';

const BAD_REQUEST = 400;
const UNAUTHORIZED = 401;
const FORBIDDEN = 403;
const NOT_FOUND = 404;
const PAYLOAD_TOO_LARGE = 413;
const UNPROCESSABLE_ENTITY = 422;
const INTERNAL_SERVER_ERROR = 500;

/**
 * User-facing copy for HTTP 413 (payload too large).
 * Prefer MaxFileSizeDisplay from the SPA loading state when available.
 */
export function getPayloadTooLargeMessage() {
    const max =
        typeof window !== 'undefined' && window.__INITIAL_STATE__?.MaxFileSizeDisplay
            ? window.__INITIAL_STATE__.MaxFileSizeDisplay
            : null;

    return max
        ? `Your file is too large to upload. Please keep the file size under ${max}.`
        : 'Your file is too large to upload. Please choose a smaller file.';
}

export default class ErrorPackage {
    constructor(debug) {
        this._debug = debug;
        this.id = uuid();
        this.err = null;
        this.instance = null;
        this.info = null;
        this.sentryId = null;
        this.date = new Date();

        this.shouldShowSnackbar = true;
    }

    capture(err, instance, info) {
        this.err = err;
        this.instance = instance;
        this.info = info;

        return this;
    }

    set(key, value) {
        this[key] = value;

        return this;
    }

    get(key) {
        return this[key];
    }

    isAccessDeniedError(code, exception) {
        return code === FORBIDDEN && exception && exception.includes('AccessDenied');
    }

    disableSnackbar() {
        this.shouldShowSnackbar = false;
    }

    reportToSentry() {
        // Only report to Sentry if this is not an HTTP response error
        // (those are handled by the axios interceptor now)
        if (this.err?.response) {
            // This is an HTTP error - it should be handled by axios interceptor
            // Don't double-report it here
            return this;
        }

        // For non-HTTP errors (Vue errors, JavaScript errors, etc.)
        Sentry.setTag('crash-report', 'false');
        Sentry.setTag('error_type', 'application_error');

        if (this.instance) {
            Sentry.setContext('vue_component', {
                name: this.instance.$options.name || 'Unknown',
                info: this.info,
            });
        }

        this.sentryId = Sentry.captureException(this.err);

        return this;
    }

    /**
     * Produces a user-friendly message from an Axios error object,
     * gracefully handling different codes.
     *
     * @returns {string|null} - If returns null or an empty string,
     *                          you can choose not to display a message.
     */
    massageAxiosError() {
        // Extract retry count once at the start to avoid redeclaration
        const retryCount = get(this.err, 'config.__retryCount', 0);

        // If there's no HTTP response, it might be a network error or cancellation
        if (!this.err.response) {
            // Check if this was a retry that failed
            if (retryCount > 0) {
                return 'Unable to connect to the server after multiple attempts. Please check your internet connection and try again.';
            }
            return 'A network error occurred or request was cancelled.';
        }

        const code = get(this.err, 'response.status');
        const exception = get(this.err, 'response.data.exception');

        if (this.isAccessDeniedError(code, exception)) {
            return null;
        }

        // Note: We don't report to Sentry here anymore as it's handled in axios interceptor
        // This method is now purely for generating user-friendly error messages

        const message = this.getErrorMessageForCode(code);
        // Don't add timestamp for 502 errors that are being retried (to avoid noise)
        if (code === 502 && retryCount === 0) {
            // First attempt - don't show error yet, retry will happen
            return null;
        }

        const timestamp = moment().format('LLL zz');

        return `${message}\nError occurred: ${timestamp}`;
    }

    getErrorMessageForCode(code) {
        switch (code) {
            case BAD_REQUEST:
                return (
                    get(this.err, 'response.data.message') ||
                    'Bad request. Please check your input and try again.'
                );

            case UNAUTHORIZED:
                return 'You are not authorized to perform this action. Please log in and try again.';

            case FORBIDDEN:
                return 'Access denied. You do not have permission to perform this action.';

            case NOT_FOUND:
                return get(this.err, 'response.data.message') || 'The requested resource was not found.';

            case PAYLOAD_TOO_LARGE:
                return getPayloadTooLargeMessage();

            case UNPROCESSABLE_ENTITY:
                // Use || instead of lodash.get default because lodash.get only
                // falls back for undefined, not null. Backend may return message: null.
                return (
                    get(this.err, 'response.data.message') || 'The data provided was invalid. Please check your input.'
                );

            case 502:
                // Note: This is only called after retries are exhausted (retryCount > 0)
                // because massageAxiosError returns null for first-attempt 502 errors
                return 'The server is temporarily unavailable. We tried to reconnect automatically but were unable to reach the server. Please refresh the page or try again in a moment.';

            case INTERNAL_SERVER_ERROR:
            default:
                if (code >= INTERNAL_SERVER_ERROR) {
                    return 'A server error occurred. Please try again later or contact support if the problem persists.';
                }

                return `An error occurred (HTTP ${code}). Please try again.`;
        }
    }

    /**
     * Generate a user-friendly error message suitable for display to end users.
     * This method handles both HTTP errors and general JavaScript errors.
     *
     * @returns {string|null} - User-friendly error message, or null if the error should not be displayed
     */
    getUserFriendlyMessage() {
        if (this.err?.response) {
            // This is an HTTP error - delegate to massageAxiosError
            return this.massageAxiosError();
        }

        if (this.err?.message) {
            const { message } = this.err;

            // Handle common JavaScript error patterns with user-friendly messages
            if (message.includes('Cannot read prop') || message.includes('undefined')) {
                return 'A display error occurred. Please refresh the page and try again.';
            }

            if (message.includes('Network Error') || message.includes('fetch')) {
                return 'A network error occurred. Please check your connection and try again.';
            }

            if (message.includes('ChunkLoadError') || message.includes('Loading chunk')) {
                return 'Failed to load application resources. Please refresh the page.';
            }

            if (message.includes('Script error')) {
                return 'A script error occurred. Please refresh the page and try again.';
            }

            // For other errors, provide a generic message
            return 'An unexpected error occurred. Please try again.';
        }

        // No recognizable error - return generic message
        return 'An unexpected error occurred. Please try again.';
    }

    toString() {
        return JSON.stringify(this.toJSON());
    }

    toJSON() {
        return {
            id: this.id,
            date: this.date,
            info: this.info,
            error: {
                message: get(this.err, 'message'),
                stack: get(this.err, 'stack'),
            },
            instance: this.instance ? this.instance.$options.name : null,
            sentryId: this.sentryId,
        };
    }
}
